import { useCallback } from 'react';

import { Link, Separator } from '@change/ds-core-ui';
import { ChatBubbleIcon, DecisionMakerIcon, MediaIcon, PetitionIcon, VotersIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useTracking } from '@change-corgi/core/react/tracking';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';
import { ResponsiveBox } from 'src/app/pages/petitionDetails/shared/components';

import type { SectionId } from '../../../../hooks/useScrollToSection';
import { SECTION_ID_MAPPING, useScrollToSection } from '../../../../hooks/useScrollToSection';
import { useContentSummary } from '../../hooks/useContentSummary';
import { DecisionMakersText } from '../DecisionMakersText';
import { MediaHitsText } from '../MediaHitsText';
import { SupporterVoicesText } from '../SupporterVoicesText';
import { UpdatesText } from '../UpdatesText';
import { VotersInfo } from '../VotersInfo';

const ContentSummaryItem: React.FC<{
	children: React.ReactNode;
	['data-testid']?: string;
	onClick?: () => void;
}> = ({ children, ...rest }) => {
	return <div {...rest}>{children}</div>;
};

const VotersSection: React.FC<{ voters: { percentage: number; isShown: boolean } }> = ({ voters }) => (
	<ContentSummaryItem data-testid="content-summary-voters">
		<div className="min-h-[28px] min-w-[28px]">
			<VotersIcon size={28} />
		</div>
		<VotersInfo percentage={voters.percentage} />
	</ContentSummaryItem>
);

const DecisionMakersSection: React.FC<{
	decisionMakers: {
		first: {
			displayName: string;
		};
		count: number;
	};
	scroll: (id: SectionId) => () => void;
}> = ({ decisionMakers, scroll }) => (
	<ContentSummaryItem data-testid="content-summary-decision-makers">
		<div className="h-[28px] w-[28px]">
			<DecisionMakerIcon size={28} />
		</div>
		<Link
			as="button"
			data-testid="content-summary-decision-makers-link"
			data-qa="content-summary-decision-makers-link"
			onClick={scroll(SECTION_ID_MAPPING.DECISION_MAKERS)}
			className="inline-block text-start font-normal text-typography-strong *:text-sm active:*:text-typography-strong hover:text-typography-strong hover:*:no-underline"
		>
			<DecisionMakersText firstDmName={decisionMakers.first.displayName} count={decisionMakers.count} />
		</Link>
	</ContentSummaryItem>
);

const UpdatesSection: React.FC<{
	updates: {
		count: number;
	};
	scroll: (id: SectionId) => () => void;
}> = ({ updates, scroll }) => (
	<ContentSummaryItem
		data-testid="content-summary-updates"
		data-qa="content-summary-updates"
		onClick={scroll(SECTION_ID_MAPPING.UPDATES)}
	>
		<div className="min-h-[28px] min-w-[28px]">
			<PetitionIcon size={28} />
		</div>
		<Link
			as="button"
			data-testid="content-summary-updates-link"
			data-qa="content-summary-updates-link"
			onClick={scroll(SECTION_ID_MAPPING.UPDATES)}
			className="inline-block text-start font-normal text-typography-strong *:text-sm active:*:text-typography-strong hover:text-typography-strong hover:*:no-underline"
		>
			<UpdatesText count={updates.count} />
		</Link>
	</ContentSummaryItem>
);

const MediaHitsSection: React.FC<{
	mediaHits: {
		count: number;
	};
	scroll: (id: SectionId) => () => void;
}> = ({ mediaHits, scroll }) => (
	<ContentSummaryItem data-testid="content-summary-media-mentions">
		<div className="min-h-[28px] min-w-[28px]">
			<MediaIcon size={28} />
		</div>
		<Link
			as="button"
			data-testid="content-summary-media-mentions-link"
			data-qa="content-summary-media-mentions-link"
			onClick={scroll(SECTION_ID_MAPPING.MEDIA_MENTIONS)}
			className="inline-block text-start font-normal text-typography-strong *:text-sm active:*:text-typography-strong hover:text-typography-strong hover:*:no-underline"
		>
			<MediaHitsText count={mediaHits.count} />
		</Link>
	</ContentSummaryItem>
);

const SupporterVoicesSection: React.FC<{
	supporterVoices: {
		count: number;
	};
	scroll: (id: SectionId) => () => void;
}> = ({ supporterVoices, scroll }) => (
	<ContentSummaryItem data-testid="content-summary-supporter-voices">
		<div className="min-h-[28px] min-w-[28px]">
			<ChatBubbleIcon size={28} />
		</div>
		<Link
			as="button"
			data-testid="content-summary-supporter-voices-link"
			data-qa="content-summary-supporter-voices-link"
			onClick={scroll(SECTION_ID_MAPPING.SUPPORTER_VOICES)}
			className="inline-block text-start font-normal text-typography-strong *:text-sm active:*:text-typography-strong hover:text-typography-strong hover:*:no-underline"
		>
			<SupporterVoicesText count={supporterVoices.count} />
		</Link>
	</ContentSummaryItem>
);

type Props = EmptyObject;

export const ContentSummary: React.FC<Props> = () => {
	const {
		data: { sectionIsShown, allItemsShown, decisionMakers, mediaHits, updates, voters, supporterVoices },
	} = useContentSummary();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();

	const {
		actions: { scrollToSection },
	} = useScrollToSection();
	const track = useTracking();

	const scroll = useCallback(
		(id: SectionId) => () => {
			void track('petition_page_content_summary_click', {
				petition_id: petitionId,
				link_type: id,
				ids: id === 'decision-makers' ? decisionMakers.dms.map((dm) => dm.id) : [],
			});
			scrollToSection(id);
		},
		[decisionMakers.dms, petitionId, scrollToSection, track],
	);

	if (!sectionIsShown) {
		return null;
	}

	const responsiveItemClassName = `lg:[&>*]:border-l lg:[&>*]:pl-6 lg:first-of-type:[&>*]:border-none ${allItemsShown ? 'lg:[&>*]:first-of-type:pl-2' : 'lg:first-of-type:[&>*]:pl-0'}`;
	const itemClassName = `[&>*]:border-stroke-weaker [&>*]:flex [&>*]:grow [&>*]:basis-0 [&>*]:flex-row [&>*]:items-start [&>*]:gap-3 [&>*]:border-l-0 [&>*]:pl-0 ${responsiveItemClassName}`;

	return (
		<>
			<ResponsiveBox viewportSize="small">
				<Separator role="presentation" className="mt-4" />
			</ResponsiveBox>
			<div className={cn('px-0 py-6', { 'lg:ml-8': !allItemsShown })} data-qa="content-summary-section">
				<div
					className={cn('flex flex-col justify-around gap-6', itemClassName, 'lg:flex-row')}
					data-testid="content-summary"
				>
					{voters.isShown && <VotersSection voters={voters} />}
					{decisionMakers.isShown && <DecisionMakersSection decisionMakers={decisionMakers} scroll={scroll} />}
					{supporterVoices.isShown && <SupporterVoicesSection supporterVoices={supporterVoices} scroll={scroll} />}
					{updates.isShown && <UpdatesSection updates={updates} scroll={scroll} />}
					{mediaHits.isShown && <MediaHitsSection mediaHits={mediaHits} scroll={scroll} />}
				</div>
			</div>
			<ResponsiveBox viewportSize="small">
				<Separator role="presentation" className="mb-4" />
			</ResponsiveBox>
		</>
	);
};
